import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
export type { StrategicPlan } from "@/types/entities";
import { toast } from "sonner";

export function useStrategicPlans() {
  return useQuery({
    queryKey: ["strategic-plans"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/strategic-plans", {});
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useCreateStrategicPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateStrategicPlanRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/strategic-plans", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["strategic-plans"] });
      toast.success("Strategic plan created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateStrategicPlan(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UpdateStrategicPlanRequest"]) => {
      const { data, error } = await apiClient.PUT("/v1/strategic-plans/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["strategic-plans"] });
      toast.success("Strategic plan updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteStrategicPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/strategic-plans/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["strategic-plans"] });
      toast.success("Strategic plan deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
